import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

/** Viewport width below which the chat renders its phone layout (plan RF-26). */
export const CHAT_PHONE_BREAKPOINT_PX = 768;

/** Panel class added to every dialog opened fullscreen on a phone. */
export const CHAT_FULLSCREEN_DIALOG_CLASS = 'chat-dialog--fullscreen';

/**
 * The one door every Internal Chat dialog opens through —
 * SPEC-2026-09-internal-chat-pwa (T-26 · RF-26).
 *
 * Callers keep describing the dialog they want (width, data, panel class); this
 * service decides how it fits the screen. Under the phone breakpoint a dialog takes
 * the whole viewport (dynamic viewport height, so the on-screen keyboard cannot push
 * its footer off screen) instead of a centred box narrower than the device. Above
 * it the caller's configuration is used untouched, so the desktop product looks
 * exactly as before.
 */
@Injectable({ providedIn: 'root' })
export class ChatDialogService {

  constructor(private readonly dialog: MatDialog) { }

  /** Dialogs currently open — read by the single-instance toggles. */
  get openDialogs(): MatDialogRef<unknown>[] {
    return this.dialog.openDialogs;
  }

  open<T, D = unknown, R = unknown>(component: ComponentType<T>, config: MatDialogConfig<D> = {}): MatDialogRef<T, R> {
    return this.dialog.open<T, D, R>(component, this.resolveConfig(config));
  }

  /** True while the viewport is under the phone breakpoint. */
  isPhoneViewport(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(`(max-width: ${CHAT_PHONE_BREAKPOINT_PX - 0.02}px)`).matches;
  }

  /** The configuration actually used — exposed for the specs. */
  resolveConfig<D>(config: MatDialogConfig<D>): MatDialogConfig<D> {
    if (!this.isPhoneViewport()) {
      return config;
    }
    const panelClass = Array.isArray(config.panelClass)
      ? config.panelClass
      : config.panelClass ? [config.panelClass] : [];
    return {
      ...config,
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      panelClass: [...panelClass, CHAT_FULLSCREEN_DIALOG_CLASS],
    };
  }
}
